import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { AnalyticsDashboard } from '../analytics/AnalyticsDashboard'
import { ChatHistoryView } from '../queue/ChatHistoryView'
import { MonthlyReport } from '../chat-history/MonthlyReport'
import { ChatHistoryCompaniesView } from '../chat-history/ChatHistoryCompaniesView'

/** Simple landing shown for agent-tier users (who cannot see Analytics). */
const SupportOverview: React.FC = () => {
  const navigate = useNavigate()
  const links: Array<{ label: string; to: string; desc: string }> = [
    { label: 'Chats', to: '/queue', desc: 'The live conversation queue' },
    { label: 'My Chats', to: '/my-chats', desc: 'Conversations assigned to you' },
    { label: 'Tickets', to: '/tickets', desc: 'Email + chat support tickets' },
  ]
  return (
    <div className="p-6">
      <h2 className="text-base font-semibold text-txt-primary mb-1">Welcome back</h2>
      <p className="text-sm text-txt-secondary mb-5">Jump into your support work.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        {links.map((l) => (
          <button
            key={l.to}
            onClick={() => navigate(l.to)}
            className="text-left p-4 rounded-xl bg-surface border border-border hover:bg-surface-hover transition-colors"
          >
            <p className="text-sm font-semibold text-txt-primary">{l.label}</p>
            <p className="text-xs text-txt-tertiary mt-0.5">{l.desc}</p>
          </button>
        ))}
      </div>
    </div>
  )
}

type Tab = 'dashboard' | 'history' | 'monthly' | 'complete'
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'history', label: 'Chat History' },
  { key: 'monthly', label: 'Monthly Report' },
  { key: 'complete', label: 'Complete' },
]

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-brand-600 text-brand-700 dark:text-brand-400 dark:border-brand-400'
        : 'border-transparent text-txt-secondary hover:text-txt-primary'
    }`}
  >
    {children}
  </button>
)

/**
 * The "Dashboard" screen. Tabbed (Lee, Oct 2026): the main Dashboard sits next
 * to Chat History, Monthly Report and Complete. The tab is reflected in the URL
 * (?tab=) so each is deep-linkable and the old /history and /chat-history links
 * redirect straight to the matching tab.
 *  • Dashboard — admin/supervisor get the Analytics overview, others a landing.
 *  • Chat History — the live list of past chats (formerly /history).
 *  • Monthly Report / Complete — the former /chat-history page, pulled up as tabs.
 */
export const DashboardHome: React.FC = () => {
  const { currentAgent } = useAuth()
  const isSupervisor = currentAgent?.role === 'supervisor' || currentAgent?.role === 'admin'
  const [params, setParams] = useSearchParams()

  const raw = (params.get('tab') || 'dashboard') as Tab
  const tab: Tab = TABS.some((t) => t.key === raw) ? raw : 'dashboard'
  const setTab = (t: Tab) => setParams(t === 'dashboard' ? {} : { tab: t }, { replace: true })

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 pt-4 bg-surface border-b border-border shrink-0">
        <h1 className="text-xl font-bold text-txt-primary">Dashboard</h1>
        <div className="flex items-center gap-1 mt-3">
          {TABS.map((t) => (
            <TabButton key={t.key} active={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</TabButton>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {/* History manages its own full-height scroll; the others sit in a scroll container. */}
        {tab === 'history' ? (
          <ChatHistoryView />
        ) : (
          <div className="h-full overflow-y-auto">
            {tab === 'dashboard' && (isSupervisor ? <AnalyticsDashboard /> : <SupportOverview />)}
            {tab === 'monthly' && <div className="p-6 max-w-7xl mx-auto"><MonthlyReport /></div>}
            {tab === 'complete' && <div className="p-6 max-w-7xl mx-auto"><ChatHistoryCompaniesView /></div>}
          </div>
        )}
      </div>
    </div>
  )
}
